import React, { useState, useEffect, useRef, useCallback } from 'react';
import CustomSelect from '../components/shared/CustomSelect';
import HelpButton from '../components/shared/HelpButton';
import ContractViewer from '../components/ContractViewer';
import { useUser } from '../utils/UserContext';
import { rolesAccess } from '../data/map';
import { cssUrl } from '../utils/cssVersion';


const displayDate = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};
// --- Constants ---
const EMPLOYMENT_STATUS_OPTIONS = ['TFN', 'ABN'];

const LEAVE_TYPE_OPTIONS = [
  'Holidays', 'Sick Leave', 'Carers Leave', 'Other Leave',
  'Compassionate', 'Work Cover', 'Termination',
];

const APPROVAL_OPTIONS = [
  'Approved', 'Not Approved', 'Cancelled by Employee',
  'Pending', 'Approved Without Pay',
];

// Leave types that don't track balances
const NO_BALANCE_LEAVE = ['Other Leave', 'Compassionate', 'Work Cover'];
// Leave types that use personal (sick/carers) balance
const PERSONAL_BALANCE_LEAVE = ['Sick Leave', 'Carers Leave'];
// Leave types that show time-worked field
const TIME_WORKED_LEAVE = ['Sick Leave', 'Carers Leave'];

// Empty per-day hours: [Sun, Mon, Tue, Wed, Thu, Fri, Sat] (matches Date.getDay())
const EMPTY_DAY_HOURS = [0, 0, 0, 0, 0, 0, 0];

// ===== Rostered shift rows (same feeds NewReplacement uses) =====
// Row: [id, name, site, shiftHours, Mon, Tue, Wed, Thu, Fri, Sat, Sun, daystatus]
const DAY_DATA_OFFSET = 4;

// ===== HELPER: Sites a person is rostered at (the supervisor picks from these) =====
const getSitesForOwner = (ownerName, shiftData) => {
  const sites = new Set();
  for (const row of shiftData) {
    if (row[1] === ownerName && row[2]) sites.add(row[2]);
  }
  return [...sites];
};

// ===== HELPER: Shift layouts (hours per weekday) for a person at a site =====
const getShiftLayouts = (ownerName, site, shiftData) => {
  const layouts = [];
  for (const row of shiftData) {
    if (row[1] === ownerName && row[2] === site) {
      const days = [];
      for (let i = 0; i < 7; i++) days.push(Number(row[DAY_DATA_OFFSET + i]) || 0);
      layouts.push({ hours: days, shiftHours: Number(row[3]) || 0, daystatus: row[11] || '' });
    }
  }
  return layouts;
};

// ===== HELPER: Parse YYYY-MM-DD as local noon (avoids UTC/DST issues) =====
const parseLocalDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
};

// ===== HELPER: Sum per-day hours across an inclusive date range =====
// dayHours: [sun, mon, tue, wed, thu, fri, sat] matching Date.getDay()
// Mirrors the Airtable Weekdays/Weekends formulas: each day in the range
// contributes that weekday's hours (0 for days the employee doesn't work).
const calcPayableHours = (startStr, endStr, dayHours) => {
  if (!startStr || !endStr || !Array.isArray(dayHours)) return 0;
  const start = parseLocalDate(startStr);
  const end = parseLocalDate(endStr);
  if (!start || !end || end < start) return 0;
  let total = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    total += Number(dayHours[cursor.getDay()]) || 0;
    cursor.setDate(cursor.getDate() + 1);
  }
  return total;
};

// ===== HELPER: Get last day of a month =====
const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// ===== HELPER: Which pay half does a day fall in? 1 = 1st–15th, 2 = 16th–end =====
const getPayHalf = (dayOfMonth) => (dayOfMonth <= 15 ? 1 : 2);

// ===== HELPER: Is a YYYY-MM-DD string in a prior month relative to today? =====
const isPriorMonth = (dateStr) => {
  if (!dateStr) return false;
  const [y, m] = dateStr.split('-').map(Number);
  const now = new Date();
  return y < now.getFullYear() || (y === now.getFullYear() && m - 1 < now.getMonth());
};

// ===== HELPER: Is a YYYY-MM-DD string in the current month/year? =====
const isCurrentMonth = (dateStr) => {
  if (!dateStr) return false;
  const [y, m] = dateStr.split('-').map(Number);
  const now = new Date();
  return y === now.getFullYear() && m - 1 === now.getMonth();
};

// ===== HELPER: Validate date range (same month + bi-monthly pay-half) =====
const validateDateRange = (startStr, endStr, payStatus) => {
  if (!startStr) return { valid: false, message: 'Start date is required' };

  const [sy, sm, sd] = startStr.split('-').map(Number);
  const start = parseLocalDate(startStr);

  if (endStr) {
    const [ey, em, ed] = endStr.split('-').map(Number);
    const end = parseLocalDate(endStr);

    if (end < start) {
      return { valid: false, message: 'End date cannot be before start date' };
    }

    // Must be same month
    if (sm !== em || sy !== ey) {
      return { valid: false, message: 'Start and end dates must be in the same month' };
    }

    // Bi-Monthly: must stay within same pay half (1st–15th or 16th–end)
    if (payStatus === 'Bi-Monthly') {
      const startHalf = getPayHalf(sd);
      const endHalf = getPayHalf(ed);
      if (startHalf !== endHalf) {
        const lastDay = getLastDayOfMonth(sy, sm - 1);
        return {
          valid: false,
          message: `Bi-Monthly: dates must be within 1st–15th or 16th–${lastDay}th`,
        };
      }
    }
  }

  return { valid: true, message: '' };
};

// ===== HELPER: Clamp a date to the same pay-half as the anchor date =====
// Returns { clamped: 'YYYY-MM-DD', didClamp: bool, boundaryDay: number, overflowDay: number }
// e.g. anchor=2026-05-10 (half 1), target=2026-05-17 → clamps to 2026-05-15
//      anchor=2026-05-20 (half 2), target=2026-05-10 → clamps to 2026-05-16
const clampToPayHalf = (anchorStr, targetStr) => {
  if (!anchorStr || !targetStr) return { clamped: targetStr, didClamp: false };
  const [ay, am, ad] = anchorStr.split('-').map(Number);
  const [ty, tm, td] = targetStr.split('-').map(Number);

  // Different month — clamping doesn't apply here; same-month is enforced separately
  if (ay !== ty || am !== tm) return { clamped: targetStr, didClamp: false };

  const anchorHalf = getPayHalf(ad);
  const targetHalf = getPayHalf(td);
  if (anchorHalf === targetHalf) return { clamped: targetStr, didClamp: false };

  // Snap to nearest boundary of the anchor's half
  const lastDay = getLastDayOfMonth(ay, am - 1);
  const boundaryDay = anchorHalf === 1 ? 15 : 16;
  const pad = (n) => String(n).padStart(2, '0');
  const clamped = `${ay}-${pad(am)}-${pad(boundaryDay)}`;
  return { clamped, didClamp: true, boundaryDay, overflowDay: td, lastDay };
};

// ===== HELPER: Bi-monthly range label =====
const getBiMonthlyRangeLabel = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const lastDay = getLastDayOfMonth(y, m - 1);
  const half = getPayHalf(d);
  const monthName = new Date(y, m - 1, 1).toLocaleString('default', { month: 'long' });
  return half === 1
    ? `1st – 15th ${monthName} ${y}`
    : `16th – ${lastDay}th ${monthName} ${y}`;
};

// ===== MAIN COMPONENT =====
const NewRequestLeave = () => {
  const hasFetched = useRef(false);

  // --- User & role ---
  const { user } = useUser();
  const isAdmin = rolesAccess.admin.includes(user?.userJob || '');

  // --- Data lists ---
  const [isLoading, setIsLoading] = useState(true);
  const [tfnEmployees, setTfnEmployees] = useState([]);
  const [tfnEmployeeData, setTfnEmployeeData] = useState([]);
  const [abnEmployees, setAbnEmployees] = useState([]);
  const [abnEmployeeData, setAbnEmployeeData] = useState([]);
  const [sitesList, setSitesList] = useState([]);          // every periodical site — fallback only
  const [tfnShiftData, setTfnShiftData] = useState([]);    // rostered shifts (TFN): who works where, hours per day
  const [abnShiftData, setAbnShiftData] = useState([]);    // rostered shifts (ABN)
  const [availableSites, setAvailableSites] = useState([]); // the selected person's sites
  const [sitesFromRoster, setSitesFromRoster] = useState(true);
  const [shiftLayouts, setShiftLayouts] = useState([]);    // TFN: shifts at the chosen site
  const [activeLayout, setActiveLayout] = useState(null);

  // --- Form state ---
  const [formData, setFormData] = useState({
    employmentstatus: '',
    typeofleave: '',
    startdate: '',
    returndate: '',
    searchemployee: '',
    email: '',
    siteworking: '',
    timeworked: '0',
    approval: '',
  });

  // --- Employee balance data ---
  const [balanceData, setBalanceData] = useState({
    daysAWeek: 0,
    hoursADay: 0,
    annualBalance: 0,
    personalBalance: 0,
    annualBalanceCurrent: 0,
    personalBalanceCurrent: 0,
    payStatus: 'Monthly',
    dayHours: EMPTY_DAY_HOURS,
  });

  // --- Calculated display values ---
  const [currentUsed, setCurrentUsed] = useState(0);
  const [leaveBalance, setLeaveBalance] = useState(0);
  const [updatedBalance, setUpdatedBalance] = useState(0);
  const [balanceStatus, setBalanceStatus] = useState('ok');

  const previousTimeRef = useRef(0);

  // --- UI state ---
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // --- Ref for auto-scrolling form fields ---
  const formFieldsRef = useRef(null);
  const lastSectionRef = useRef(null);

  // Track which sections are visible to detect new reveals
  const prevSectionsRef = useRef('');

  // Gently scroll new sections into view (not on every keystroke)
  useEffect(() => {
    const sectionKey = [
      formData.employmentstatus ? 'emp' : '',
      formData.typeofleave ? 'leave' : '',
      formData.searchemployee ? 'name' : '',
    ].join('-');

    if (sectionKey !== prevSectionsRef.current && prevSectionsRef.current !== '') {
      if (lastSectionRef.current) {
        setTimeout(() => {
          lastSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 150);
      }
    }
    prevSectionsRef.current = sectionKey;
  }, [formData.employmentstatus, formData.typeofleave, formData.searchemployee]);

  // ─── Load stylesheets ───
  useEffect(() => {
    const link1 = document.createElement('link');
    link1.rel = 'stylesheet';
    link1.href = cssUrl('newuseronboarding.css');
    document.head.appendChild(link1);

    const link2 = document.createElement('link');
    link2.rel = 'stylesheet';
    link2.href = cssUrl('filterTemplate.css');
    document.head.appendChild(link2);

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
    };
  }, []);

  // ─── Fetch data ───
  useEffect(() => {
    if (hasFetched.current) return;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [tfnRes, abnRes, sitesRes, tfnShiftRes, abnShiftRes] = await Promise.all([
          fetch('/api/leave/tfn-employees'),
          fetch('/api/leave/abn-employees'),
          fetch('/my/sites'),
          fetch('/api/replacement/tfn-shifts'),   // rostered shifts — the same feed NewReplacement uses
          fetch('/api/replacement/abn-shifts'),
        ]);
        const [tfnData, abnData, sitesData, tfnShifts, abnShifts] = await Promise.all([
          tfnRes.json(),
          abnRes.json(),
          sitesRes.json(),
          tfnShiftRes.json().catch(() => ({})),
          abnShiftRes.json().catch(() => ({})),
        ]);
        setTfnShiftData(Array.isArray(tfnShifts.all) ? tfnShifts.all : []);
        setAbnShiftData(Array.isArray(abnShifts.all) ? abnShifts.all : []);
        setTfnEmployees(Array.isArray(tfnData.names) ? tfnData.names : []);
        setTfnEmployeeData(Array.isArray(tfnData.all) ? tfnData.all : []);
        setAbnEmployees(Array.isArray(abnData.names) ? abnData.names : []);
        setAbnEmployeeData(Array.isArray(abnData.all) ? abnData.all : []);
        setSitesList(Array.isArray(sitesData) ? sitesData : (sitesData.sites || []));
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    hasFetched.current = true;
  }, []);

  // ─── Toast auto-dismiss ───
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ─────────────────────────────────────────────
  //  FIELD VISIBILITY
  // ─────────────────────────────────────────────
  const { employmentstatus, typeofleave } = formData;
  const isTFN = employmentstatus === 'TFN';
  const isABN = employmentstatus === 'ABN';
  const hasEmploymentStatus = isTFN || isABN;
  const hasLeaveType = hasEmploymentStatus && typeofleave !== '';
  const isBiMonthly = balanceData.payStatus === 'Bi-Monthly';

  const showLeaveType = hasEmploymentStatus;
  const showEmployee = hasLeaveType;
  const showEmail = hasLeaveType && formData.email !== '';
  const showDates = hasLeaveType;
  const showReturnDate = hasLeaveType && typeofleave !== 'Termination';
  const showSite = hasLeaveType;
  const showTimeWorked = hasLeaveType && TIME_WORKED_LEAVE.includes(typeofleave);
  const showBalances = hasLeaveType && isTFN && !NO_BALANCE_LEAVE.includes(typeofleave) && typeofleave !== 'Termination';
  const showUpdatedBalance = showBalances;
  const showApproval = hasLeaveType;

  // ─────────────────────────────────────────────
  //  LEAVE BALANCE CALCULATION
  //  Per-day model: each day in the range contributes that weekday's
  //  hours from the employee's Mon–Sun Current fields — mirrors the
  //  Airtable Weekdays/Weekends formulas exactly.
  // ─────────────────────────────────────────────
  const recalculateBalances = useCallback(() => {
    if (isABN || NO_BALANCE_LEAVE.includes(typeofleave) || typeofleave === 'Termination') {
      setCurrentUsed(0);
      setLeaveBalance(0);
      setUpdatedBalance(0);
      setBalanceStatus('ok');
      return;
    }

    const { annualBalance, personalBalance, annualBalanceCurrent, personalBalanceCurrent, dayHours } = balanceData;
    const { startdate, returndate, timeworked } = formData;

    let hoursUsed = calcPayableHours(startdate, returndate, dayHours);

    if (TIME_WORKED_LEAVE.includes(typeofleave)) {
      const timeVal = parseFloat(timeworked) || 0;
      const prevTime = previousTimeRef.current;
      if (timeVal >= 0 && hoursUsed > 0) {
        if (timeVal > prevTime) {
          hoursUsed -= (timeVal - prevTime);
        } else if (timeVal < prevTime) {
          hoursUsed += (prevTime - timeVal);
        }
      }
    }

    hoursUsed = Math.max(0, hoursUsed);

    const isPersonal = PERSONAL_BALANCE_LEAVE.includes(typeofleave);
    const balance = isPersonal ? personalBalance : annualBalance;
    const balanceCurrent = isPersonal ? personalBalanceCurrent : annualBalanceCurrent;

    setCurrentUsed(parseFloat(hoursUsed.toFixed(4)));
    setLeaveBalance(balance);
    setUpdatedBalance(balanceCurrent);

    const effectiveBalance = (balance === balanceCurrent) ? balance : balanceCurrent;
    if (effectiveBalance < hoursUsed) {
      setBalanceStatus('exceeded');
      setFormData((prev) => ({ ...prev, approval: 'Not Approved' }));
    } else {
      setBalanceStatus('ok');
      setFormData((prev) => ({ ...prev, approval: 'Approved' }));
    }
  }, [balanceData, formData.startdate, formData.returndate, formData.timeworked, typeofleave, isABN]);

  useEffect(() => {
    if (showBalances && formData.searchemployee) {
      recalculateBalances();
    }
  }, [formData.startdate, formData.returndate, formData.timeworked, typeofleave, showBalances, formData.searchemployee, recalculateBalances]);

  // ─────────────────────────────────────────────
  //  FIELD HANDLERS
  // ─────────────────────────────────────────────
  const setValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => new Set([...prev, field]));
  };

  const clearDates = () => {
    setFormData((prev) => ({ ...prev, startdate: '', returndate: '' }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n.startdate;
      delete n.returndate;
      return n;
    });
  };

  const handleEmploymentStatusChange = (val) => {
    setFormData({
      employmentstatus: val, typeofleave: '', startdate: '', returndate: '',
      searchemployee: '', email: '', siteworking: '', timeworked: '0', approval: '',
    });
    setBalanceData({ daysAWeek: 0, hoursADay: 0, annualBalance: 0, personalBalance: 0, annualBalanceCurrent: 0, personalBalanceCurrent: 0, payStatus: 'Monthly', dayHours: EMPTY_DAY_HOURS });
    setAvailableSites([]);
    setSitesFromRoster(true);
    setShiftLayouts([]);
    setActiveLayout(null);
    setCurrentUsed(0);
    setLeaveBalance(0);
    setUpdatedBalance(0);
    setBalanceStatus('ok');
    previousTimeRef.current = 0;
    setErrors({});
    setTouched(new Set(['employmentstatus']));
  };

  const handleLeaveTypeChange = (val) => {
    const newApproval = isABN
      ? 'Approved Without Pay'
      : NO_BALANCE_LEAVE.includes(val) ? 'Pending'
      : val === 'Termination' ? 'Approved' : '';

    setFormData((prev) => ({
      ...prev, typeofleave: val, startdate: '', returndate: '',
      siteworking: '', timeworked: '0', approval: newApproval,
    }));
    setShiftLayouts([]);
    setActiveLayout(null);
    previousTimeRef.current = 0;
    setCurrentUsed(0);
    setErrors((prev) => { const n = { ...prev }; delete n.startdate; delete n.returndate; return n; });
    setTouched((prev) => new Set([...prev, 'typeofleave']));
  };

  const handleEmployeeSelect = (name) => {
    setValue('searchemployee', name);

    const isTfn = formData.employmentstatus === 'TFN';
    const nameList = isTfn ? tfnEmployees : abnEmployees;
    const dataList = isTfn ? tfnEmployeeData : abnEmployeeData;
    const idx = nameList.indexOf(name);

    if (idx >= 0 && dataList[idx]) {
      const row = dataList[idx];
      const email = row[2] || '';

      if (isTfn) {
        const payRaw = row[9] || 'Monthly';
        const payStatus = (typeof payRaw === 'string' && payRaw.includes('Bi-Monthly'))
          ? 'Bi-Monthly' : 'Monthly';

        setBalanceData({
          hoursADay: Number(row[5]) || 0,
          daysAWeek: Number(row[6]) || 0,
          annualBalance: Number(row[3]) || 0,
          personalBalance: Number(row[4]) || 0,
          annualBalanceCurrent: Number(row[7]) || 0,
          personalBalanceCurrent: Number(row[8]) || 0,
          payStatus,
          // Per-day hours from backend row positions 10–16
          // (Mon, Tue, Wed, Thu, Fri, Sat, Sun) reordered to
          // getDay() order: [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
          dayHours: [
            Number(row[16]) || 0,  // Sun
            Number(row[10]) || 0,  // Mon
            Number(row[11]) || 0,  // Tue
            Number(row[12]) || 0,  // Wed
            Number(row[13]) || 0,  // Thu
            Number(row[14]) || 0,  // Fri
            Number(row[15]) || 0,  // Sat
          ],
        });
      } else {
        setBalanceData((prev) => ({ ...prev, payStatus: 'Monthly', dayHours: EMPTY_DAY_HOURS }));
      }

      previousTimeRef.current = 0;
      setFormData((prev) => ({
        ...prev, searchemployee: name, email, timeworked: '0',
        siteworking: '', startdate: '', returndate: '',
      }));
      setErrors((prev) => { const n = { ...prev }; delete n.startdate; delete n.returndate; return n; });
    }

    // Site Working lists only the sites this person is rostered at (ABN and TFN alike — the
    // same rule as NewReplacement). If the roster has no rows for the name, every site is
    // offered so the request can still be lodged.
    const rostered = getSitesForOwner(name, isTfn ? tfnShiftData : abnShiftData);
    setAvailableSites(rostered.length ? rostered : sitesList);
    setSitesFromRoster(rostered.length > 0);
    setShiftLayouts([]);
    setActiveLayout(null);
  };

  // Site chosen: for a TFN employee show the rostered shift at that site (hours per weekday),
  // as NewReplacement does. Informational only — the leave balance still uses the employee's
  // own Mon–Sun hours, so the request-leave rules are unchanged.
  const handleSiteChange = (val) => {
    setValue('siteworking', val);
    if (isTFN) {
      const layouts = getShiftLayouts(formData.searchemployee, val, tfnShiftData);
      setShiftLayouts(layouts);
      setActiveLayout(layouts.length === 1 ? 0 : null);
    } else {
      setShiftLayouts([]);
      setActiveLayout(null);
    }
  };

  const shiftOptions = shiftLayouts.map((l, i) => {
    const wk = Math.max(...l.hours.slice(0, 5)), we = Math.max(...l.hours.slice(5));
    return `Shift ${i + 1} · ${wk} hrs weekdays${we > 0 ? ` · ${we} hrs weekends` : ''}${l.daystatus ? ` · ${l.daystatus}` : ''}`;
  });
  const activeShift = activeLayout !== null ? shiftLayouts[activeLayout] : null;
  const shiftWeekdayHrs = activeShift ? Math.max(...activeShift.hours.slice(0, 5)) : 0;
  const shiftWeekendHrs = activeShift ? Math.max(...activeShift.hours.slice(5)) : 0;

  // ─────────────────────────────────────────────
  //  DATE HANDLERS — anchor-based
  // ─────────────────────────────────────────────
  //
  //  Rules:
  //  • Admin can pick any date (including prior months).
  //  • Non-admin: selecting a prior month → toast + clear BOTH dates.
  //  • START is the anchor. Changing start always resets end to match start;
  //    the user then extends end forward as needed.
  //  • END cannot go before start (toast + reject — user must change start
  //    to shift the range backward).
  //  • END cannot leave start's month (clamped to EOM with a toast).
  //  • Bi-Monthly: end cannot leave start's pay-half (clamped to 15th or EOM
  //    with a toast suggesting a separate request for the remainder).
  //
  const handleStartDateChange = (val) => {
    if (!val) return;

    // Prior-month gate (non-admin only)
    if (!isAdmin && isPriorMonth(val)) {
      setToast({ type: 'error', message: 'Cannot request leave for a prior month' });
      clearDates();
      return;
    }

    // Start is the anchor — any change to start resets end to match start.
    // The user then extends end forward (within month / pay-half) as needed.
    const endVal = val;

    setFormData((prev) => ({ ...prev, startdate: val, returndate: endVal }));
    setTouched((prev) => new Set([...prev, 'startdate', 'returndate']));
    setErrors((prev) => { const n = { ...prev }; delete n.startdate; delete n.returndate; return n; });
  };

  const handleReturnDateChange = (val) => {
    if (!val) return;

    // Prior-month gate (non-admin only)
    if (!isAdmin && isPriorMonth(val)) {
      setToast({ type: 'error', message: 'Cannot request leave for a prior month' });
      clearDates();
      return;
    }

    let startVal = formData.startdate;
    let endVal = val;

    // First-set auto-match: no start yet → start mirrors to end.
    // After this, start becomes the anchor; further end changes follow anchor rules.
    if (!startVal) {
      startVal = val;
      setFormData((prev) => ({ ...prev, startdate: startVal, returndate: endVal }));
      setTouched((prev) => new Set([...prev, 'startdate', 'returndate']));
      setErrors((prev) => { const n = { ...prev }; delete n.startdate; delete n.returndate; return n; });
      return;
    }

    // ── Anchor-based rules: start is the anchor, end is constrained ──

    // Floor: end cannot go before start. Change start instead.
    if (endVal < startVal) {
      setToast({
        type: 'error',
        message: 'End date cannot be before start date. Change the start date to shift the range.',
      });
      return;
    }

    // Ceiling: end must stay in the same month as start.
    const [sy, sm] = startVal.split('-').map(Number);
    const [ey, em] = endVal.split('-').map(Number);
    if (sy !== ey || sm !== em) {
      const lastDay = getLastDayOfMonth(sy, sm - 1);
      const pad = (n) => String(n).padStart(2, '0');
      const eomStr = `${sy}-${pad(sm)}-${pad(lastDay)}`;
      setToast({
        type: 'error',
        message: `End date must stay within the same month. Clamped to ${lastDay}th.`,
      });
      endVal = eomStr;
    }

    // Bi-Monthly ceiling: end must stay in start's pay-half.
    if (balanceData.payStatus === 'Bi-Monthly') {
      const clampResult = clampToPayHalf(startVal, endVal);
      if (clampResult.didClamp) {
        const { boundaryDay, overflowDay, lastDay } = clampResult;
        endVal = clampResult.clamped;
        const remainStart = boundaryDay === 15 ? 16 : 1;
        const remainEnd = boundaryDay === 15 ? lastDay : 15;
        setToast({
          type: 'error',
          message: `Bi-Monthly: end clamped to ${boundaryDay}th. Create a separate request for ${remainStart}–${remainEnd} to cover up to the ${overflowDay}th.`,
        });
      }
    }

    setFormData((prev) => ({ ...prev, startdate: startVal, returndate: endVal }));
    setTouched((prev) => new Set([...prev, 'startdate', 'returndate']));
    setErrors((prev) => { const n = { ...prev }; delete n.startdate; delete n.returndate; return n; });
  };

  const handleTimeWorkedChange = (e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    const num = parseFloat(val);
    if (val && !isNaN(num) && num < 0) {
      setToast({ type: 'error', message: 'Time worked must be zero or greater' });
      return;
    }
    setValue('timeworked', val);
  };

  const handleApprovalChange = (val) => {
    if (isABN) { setValue('approval', 'Approved Without Pay'); return; }
    if (NO_BALANCE_LEAVE.includes(typeofleave) && val !== 'Pending') {
      setToast({ type: 'error', message: 'Only "Pending" is allowed for this leave type' });
      return;
    }
    if (balanceStatus === 'exceeded' && val !== 'Approved Without Pay') {
      setToast({ type: 'error', message: 'Balance exceeded — only "Approved Without Pay" is allowed' });
      return;
    }
    setValue('approval', val);
  };

  // ─────────────────────────────────────────────
  //  VALIDATION & SUBMIT
  // ─────────────────────────────────────────────
  const isFormValid = () => {
    if (!formData.employmentstatus) return false;
    if (!formData.typeofleave) return false;
    if (!formData.searchemployee) return false;
    if (showApproval && !formData.approval) return false;
    if (showSite && !formData.siteworking) return false;

    if (showDates && !formData.startdate) return false;
    if (showReturnDate && !formData.returndate) return false;

    if (showTimeWorked) {
      const tw = formData.timeworked;
      if (tw === '' || tw === undefined || tw === null) return false;
      const twNum = parseFloat(tw);
      if (isNaN(twNum) || twNum < 0) return false;
    }

    if (Object.keys(errors).length > 0) return false;

    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid() || isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      ...formData,
      currentused: currentUsed,
      leavebalance: leaveBalance,
      updatedbalance: updatedBalance,
      paystatus: balanceData.payStatus,
    };

    try {
      const response = await fetch('/processleave', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        setToast({ type: 'success', message: 'Leave request submitted successfully!' });
        setTimeout(() => { window.history.back(); }, 2000);
      } else {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Submission failed');
      }
    } catch (err) {
      console.error('Submit failed:', err);
      setToast({ type: 'error', message: err.message || 'Failed to submit leave request' });
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `ob-input ${touched.has(field) && errors[field] ? 'ob-input-error' : ''}`;

  const balanceColor = (value, threshold) =>
    (threshold > 0 && value < threshold) ? '#f87171' : '#22d3ee';

  // ─── Loading ───
  if (isLoading) {
    return (
      <div className="ob-page">
        <div className="ob-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="fr-loading-state">
            <div className="fr-spinner" />
            <div className="fr-loading-text">Loading Request Leave Form…</div>
          </div>
        </div>
      </div>
    );
  }

  const employeeOptions = isTFN ? tfnEmployees : isABN ? abnEmployees : [];

  return (
    <div className="ob-page">
      {toast && (
        <div className={`ob-toast ${toast.type === 'success' ? 'ob-toast-success' : 'ob-toast-error'}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === 'success'
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />}
          </svg>
          {toast.message}
        </div>
      )}

      <div className="ob-container">
        <div className="ob-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h1 className="ob-title">Request Leave</h1>
            <p className="ob-subtitle">Fill in the details to submit a leave request</p>
          </div>
          <HelpButton helpData={[
            { chip: 'requestleavenew', urlFilter: '/static/pdffilters/LeaveRequestNew.pdf' }
          ]} />
        </div>

        {pdfUrl && (
          <ContractViewer pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} />
        )}
        <div className="ob-form-card">
          <div className="ob-form-fields" ref={formFieldsRef} style={{ minHeight: '50vh', maxHeight: '80vh' }}>

            {/* ─── Employment Status ─── */}
            <div className="ob-field">
              <CustomSelect
                label="Employment Status"
                value={formData.employmentstatus}
                onChange={handleEmploymentStatusChange}
                options={EMPLOYMENT_STATUS_OPTIONS}
              />
            </div>

            {/* ─── Type of Leave ─── */}
            {showLeaveType && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <CustomSelect
                    label="Type of Leave"
                    value={formData.typeofleave}
                    onChange={handleLeaveTypeChange}
                    options={LEAVE_TYPE_OPTIONS}
                  />
                </div>
              </>
            )}

            {/* ─── Employee Search ─── */}
            {showEmployee && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <CustomSelect
                    label={isTFN ? 'Search Employee' : 'Search Subbie'}
                    value={formData.searchemployee}
                    onChange={handleEmployeeSelect}
                    options={employeeOptions}
                  />
                </div>
              </>
            )}

            {/* ─── Email ─── */}
            {showEmail && (
              <div className="ob-field">
                <label className="ob-label">Email</label>
                <input type="email" value={formData.email} readOnly className="ob-input ob-input-readonly" />
              </div>
            )}

            {/* ─── Pay Status Badge ─── */}
            {formData.searchemployee && isTFN && (
              <div className="ob-field">
                <label className="ob-label">Pay Cycle</label>
                <div className="ob-badge-field">
                  <span className={`ob-badge ${isBiMonthly ? 'ob-badge-amber' : 'ob-badge-teal'}`}>
                    {balanceData.payStatus}
                  </span>
                </div>
                {isBiMonthly && (
                  <div className="ob-helper">
                    Bi-Monthly employees can only request leave within 1st–15th or 16th–end of the month
                  </div>
                )}
              </div>
            )}

            {/* ─── Dates ─── */}
            {showDates && (
              <>
                <div className="ob-divider" />

                <div className="ob-field">
                  <label className="ob-label">
                    Request Leave Date <span className="ob-label-required">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.startdate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className={inputClass('startdate')}
                  />
                  {isBiMonthly && formData.startdate && (
                    <div className="ob-helper">
                      Allowed range: {getBiMonthlyRangeLabel(formData.startdate)}
                    </div>
                  )}
                </div>

                {showReturnDate && (
                  <div className="ob-field">
                    <label className="ob-label">
                      Last Day On Leave <span className="ob-label-required">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.returndate}
                      onChange={(e) => handleReturnDateChange(e.target.value)}
                      className={inputClass('returndate')}
                    />
                  </div>
                )}
              </>
            )}

            {/* ─── Site Working — only the sites this person is rostered at ─── */}
            {showSite && (
              <div className="ob-field">
                <CustomSelect
                  label="Site Working"
                  value={formData.siteworking}
                  onChange={handleSiteChange}
                  options={formData.searchemployee ? availableSites : []}
                />
                {!formData.searchemployee && (
                  <div className="ob-helper">Choose the {isTFN ? 'employee' : 'subbie'} first — only their rostered sites are listed</div>
                )}
                {formData.searchemployee && !sitesFromRoster && (
                  <div className="ob-helper">No rostered sites found for {formData.searchemployee} — showing every site</div>
                )}
              </div>
            )}

            {/* ─── TFN: the rostered shift at that site (same view as Replacement) ─── */}
            {showSite && isTFN && formData.siteworking && shiftLayouts.length > 1 && (
              <div className="ob-field">
                <CustomSelect
                  label="Select Shift"
                  value={activeLayout !== null ? shiftOptions[activeLayout] : ''}
                  onChange={(val) => setActiveLayout(shiftOptions.indexOf(val) >= 0 ? shiftOptions.indexOf(val) : null)}
                  options={shiftOptions}
                />
                <div className="ob-helper">{formData.searchemployee} has more than one shift at this site</div>
              </div>
            )}
            {showSite && isTFN && activeShift && (
              <>
                <div className="ob-field">
                  <label className="ob-label">Weekday Hours</label>
                  <div className="ob-input ob-input-readonly" style={{ color: '#22d3ee' }}>{shiftWeekdayHrs} hrs</div>
                </div>
                {shiftWeekendHrs > 0 && (
                  <div className="ob-field">
                    <label className="ob-label">Weekend Hours</label>
                    <div className="ob-input ob-input-readonly" style={{ color: '#22d3ee' }}>{shiftWeekendHrs} hrs</div>
                  </div>
                )}
              </>
            )}

            {/* ─── Time Worked ─── */}
            {showTimeWorked && (
              <div className="ob-field">
                <label className="ob-label">Time Worked (hours)</label>
                <input
                  type="text" inputMode="decimal" value={formData.timeworked}
                  onChange={handleTimeWorkedChange} placeholder="0"
                  className={inputClass('timeworked')}
                />
                <div className="ob-helper">Hours actually worked on this day — adjusts leave balance accordingly</div>
              </div>
            )}

            {/* ─── Leave Balances ─── */}
            {showBalances && formData.searchemployee && (
              <>
                <div className="ob-divider" />

                <div className="ob-field">
                  <label className="ob-label">Current Leave Used</label>
                  <div className="ob-input ob-input-readonly"
                    style={{ color: balanceStatus === 'exceeded' ? '#f87171' : '#22d3ee' }}>
                    {currentUsed.toFixed(4)} hours
                  </div>
                </div>

                <div className="ob-field">
                  <label className="ob-label">Leave Balance</label>
                  <div className="ob-input ob-input-readonly"
                    style={{ color: balanceColor(leaveBalance, currentUsed) }}>
                    {leaveBalance.toFixed(4)} hours
                  </div>
                </div>

                {showUpdatedBalance && (
                  <div className="ob-field">
                    <label className="ob-label">Updated Balance (used this month)</label>
                    <div className="ob-input ob-input-readonly"
                      style={{ color: balanceColor(updatedBalance, currentUsed) }}>
                      {updatedBalance.toFixed(4)} hours
                    </div>
                  </div>
                )}

                {balanceStatus === 'exceeded' && (
                  <div className="ob-info-banner ob-info-banner-warning">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86l-8.6 14.86a1 1 0 00.86 1.51h17.18a1 1 0 00.86-1.51l-8.6-14.86a1 1 0 00-1.72 0z" />
                    </svg>
                    Leave balance exceeded — approval set to "Not Approved". Change to "Approved Without Pay" if applicable.
                  </div>
                )}
              </>
            )}

            {/* ─── Approval ─── */}
            {showApproval && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <CustomSelect
                    label="Leave Approval"
                    value={formData.approval}
                    onChange={handleApprovalChange}
                    options={APPROVAL_OPTIONS}
                  />
                  {isABN && <div className="ob-helper">ABN employees are automatically set to "Approved Without Pay"</div>}
                  {NO_BALANCE_LEAVE.includes(typeofleave) && isTFN && <div className="ob-helper">This leave type defaults to "Pending" for approval</div>}
                </div>
              </>
            )}

            {/* ─── Summary Card ─── */}
            {formData.searchemployee && formData.typeofleave && formData.startdate && (
              <>
                <div className="ob-divider" />
                <div className="ob-record-card">
                  <div className="ob-record-header">
                    <div className="ob-record-icon">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="ob-record-title">Leave Request Summary</h3>
                  </div>
                  <div className="ob-record-grid">
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Employee</div>
                      <div className="ob-record-item-value">{formData.searchemployee}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Type</div>
                      <div className="ob-record-item-value">{formData.typeofleave}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Status</div>
                      <div className="ob-record-item-value">{formData.employmentstatus}</div>
                    </div>
                    {isTFN && (
                      <div className="ob-record-item">
                        <div className="ob-record-item-label">Pay Cycle</div>
                        <div className="ob-record-item-value" style={{ color: isBiMonthly ? '#fbbf24' : '#22d3ee' }}>
                          {balanceData.payStatus}
                        </div>
                      </div>
                    )}
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Site</div>
                      <div className="ob-record-item-value">{formData.siteworking || '—'}</div>
                    </div>
                    {isTFN && activeShift && (
                      <div className="ob-record-item">
                        <div className="ob-record-item-label">Shift</div>
                        <div className="ob-record-item-value">{shiftWeekdayHrs} hrs weekdays{shiftWeekendHrs > 0 ? ` · ${shiftWeekendHrs} hrs weekends` : ''}</div>
                      </div>
                    )}
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">From</div>
                      <div className="ob-record-item-value">{displayDate(formData.startdate) || '—'}</div>
                    </div>
                    {showReturnDate && (
                      <div className="ob-record-item">
                        <div className="ob-record-item-label">To</div>
                        <div className="ob-record-item-value">{displayDate(formData.returndate) || '—'}</div>
                      </div>
                    )}
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Approval</div>
                      <div className="ob-record-item-value" style={{
                        color: formData.approval === 'Approved' ? '#4ade80'
                          : formData.approval === 'Not Approved' ? '#f87171'
                          : formData.approval === 'Pending' ? '#fbbf24' : '#22d3ee'
                      }}>
                        {formData.approval || '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ─── Scroll anchor ─── */}
            <div ref={lastSectionRef} />

            {/* ─── Submit ─── */}
            <button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              className="ob-btn ob-btn-submit"
            >
              {isSubmitting ? (
                <span className="ob-spinner">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Submitting…
                </span>
              ) : (
                'Submit Leave Request'
              )}
            </button>

          </div>
        </div>

        <p className="ob-footer">Request Leave</p>
      </div>
    </div>
  );
};

export default NewRequestLeave;